<template>
  <button
    class="flex w-full items-center gap-3 p-4 text-left font-medium transition-all active:translate-x-[2px] active:translate-y-[2px]"
    :class="[borderClass, hoverClass, colorClass]"
    @click="handleClick"
  >
    <span v-if="icon" class="text-xl">{{ icon }}</span>
    <span class="flex-1" :class="textColorClass">
      <slot>{{ label }}</slot>
    </span>
    <span v-if="value" :class="valueColorClass">{{ value }}</span>
    <span v-if="showChevron" :class="chevronColorClass">›</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue";

type VariantType = "default" | "danger" | "warning";

type Props = {
  icon?: string;
  label?: string;
  value?: string;
  variant?: VariantType;
  showBorder?: boolean;
  showChevron?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  variant: "default",
  showBorder: true,
  showChevron: true,
});

const emit = defineEmits<{
  (e: "click"): void;
}>();

const borderClass = computed(() =>
  props.showBorder ? "border-b-2 border-black-dark" : "",
);

const hoverClass = computed(() => {
  switch (props.variant) {
    case "danger":
      return "hover:bg-kekkle-red/10 dark:hover:bg-kekkle-red/20";
    case "warning":
      return "hover:bg-kekkle-blue/10 dark:hover:bg-kekkle-blue/20";
    default:
      return "hover:bg-kekkle-yellow/20 dark:hover:bg-kekkle-yellow/10";
  }
});

const colorClass = computed(() => {
  return "dark:bg-kekkle-dark-light";
});

const textColorClass = computed(() => {
  switch (props.variant) {
    case "danger":
      return "text-kekkle-red dark:text-kekkle-red-light";
    case "warning":
      return "text-kekkle-blue dark:text-kekkle-blue-light";
    default:
      return "text-gray-900 dark:text-slate-200";
  }
});

const valueColorClass = computed(() => {
  return "font-bold text-kekkle-yellow-dark dark:text-kekkle-yellow";
});

const chevronColorClass = computed(() => {
  switch (props.variant) {
    case "danger":
      return "text-kekkle-red";
    case "warning":
      return "text-kekkle-blue";
    default:
      return "text-gray-600 dark:text-gray-400";
  }
});

function handleClick() {
  emit("click");
}
</script>
